import type { QuestionSchema } from '../types/templateFormFields';

export interface ConditionalQuestionLike {
    name: string;
    questionSchema: Pick<QuestionSchema, 'conditions' | 'conditionedBy' | 'isConditionalQuestion'>;
}

/** Names of every question shown conditionally beneath `name`, at any depth. */
export function getConditionalDescendantNames<T extends ConditionalQuestionLike>(
    questions: readonly T[],
    name: string,
): string[] {
    const descendants: string[] = [];
    const queue = [name];
    while (queue.length > 0) {
        const parentName = queue.shift() as string;
        const parent = questions.find((question) => question.name === parentName);
        const childNames = new Set(
            (parent?.questionSchema.conditions ?? [])
                .map((condition) => condition.nextQuestion?.name)
                .filter((childName): childName is string => !!childName),
        );
        for (const question of questions) {
            if (question.questionSchema.conditionedBy === parentName) childNames.add(question.name);
        }
        for (const childName of childNames) {
            if (childName === name || descendants.includes(childName)) continue;
            descendants.push(childName);
            queue.push(childName);
        }
    }
    return descendants;
}

export interface QuestionRemoval<T> {
    questions: T[];
    /** The removed question followed by its conditional descendants. */
    removed: T[];
    /** The parent whose `conditions` no longer reference the removed question, if any. */
    updatedParent: T | undefined;
}

/** Removes a question and its conditional descendants, and unlinks it from its parent's `conditions`. */
export function removeQuestionWithConditionals<T extends ConditionalQuestionLike>(
    questions: readonly T[],
    name: string,
): QuestionRemoval<T> {
    const target = questions.find((question) => question.name === name);
    if (!target) return { questions: [...questions], removed: [], updatedParent: undefined };

    const removedNames = new Set([name, ...getConditionalDescendantNames(questions, name)]);
    const parentName = target.questionSchema.conditionedBy;
    let updatedParent: T | undefined;

    const remaining = questions
        .filter((question) => !removedNames.has(question.name))
        .map((question) => {
            if (!parentName || question.name !== parentName) return question;
            updatedParent = {
                ...question,
                questionSchema: {
                    ...question.questionSchema,
                    conditions: question.questionSchema.conditions?.filter(
                        (condition) => condition.nextQuestion?.name !== name,
                    ),
                },
            };
            return updatedParent;
        });

    const removed = [...removedNames]
        .map((removedName) => questions.find((question) => question.name === removedName))
        .filter((question): question is T => !!question);

    return { questions: remaining, removed, updatedParent };
}

export interface QuestionGroup<T> {
    question: T;
    conditionals: T[];
}

/**
 * Groups questions into top-level questions with their conditional descendants, preserving
 * array order. Conditionals whose parent is missing become their own group.
 */
export function groupQuestionsWithConditionals<T extends ConditionalQuestionLike>(
    questions: readonly T[],
): QuestionGroup<T>[] {
    const groups: QuestionGroup<T>[] = [];
    const groupByName = new Map<string, QuestionGroup<T>>();
    const names = new Set(questions.map((question) => question.name));

    for (const question of questions) {
        const parentName = question.questionSchema.conditionedBy;
        if (question.questionSchema.isConditionalQuestion && parentName && names.has(parentName)) {
            continue;
        }
        const group = { question, conditionals: [] };
        groups.push(group);
        groupByName.set(question.name, group);
    }

    for (const group of groups) {
        for (const descendantName of getConditionalDescendantNames(
            questions,
            group.question.name,
        )) {
            const descendant = questions.find((question) => question.name === descendantName);
            if (descendant && !groupByName.has(descendantName)) group.conditionals.push(descendant);
        }
        group.conditionals.sort((a, b) => questions.indexOf(a) - questions.indexOf(b));
    }

    return groups;
}
